import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SetHlp } from 'src/lib/fliko/helpers/set.helper';
import { ResidentType } from 'src/lib/fliko/models/person/resident.data';
import { DataTableConfig } from 'src/app/shared/components/clickable-row-table/models/data-table-config.models';
import { LocalizationHelper } from 'src/app/shared/helpers/localization.helper';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { ReceiverSelectDialogData, ReceiverSelectReturnData } from '../view-models/receivers-select.view-model';
import {
  receiversTableColumns,
  ReceiversTableRows,
  residentUnitVMToTableRows
} from '../view-models/receivers-select-table.model';
import { ReceiversSelectContentComponent } from '../receivers-select-content/receivers-select-content.component';

@Component({
  selector: 'app-receivers-select-dialog',
  templateUrl: './receivers-select-dialog.component.html',
  styleUrls: ['./receivers-select-dialog.component.scss']
})
export class ReceiversSelectDialogComponent implements OnInit {
  public tableConfig: DataTableConfig;
  public locHelper = new LocalizationHelper(this.ls);
  public defaultSelected = new Set<string>();

  public vmToTableRows = residentUnitVMToTableRows;

  @ViewChild(ReceiversSelectContentComponent) contentComponent!: ReceiversSelectContentComponent<ReceiversTableRows>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ReceiverSelectDialogData,
    private dialogRef: MatDialogRef<ReceiversSelectDialogComponent, ReceiverSelectReturnData>,
    private ls: LocalizationService
  ) {
    this.tableConfig = new DataTableConfig({
      dataColumns: receiversTableColumns(data.displayMode),
      searchKeyNames: ['fullName', 'email', 'phone'],
      paginator: true,
      newStyle: true
    });
  }

  ngOnInit(): void {
    const selected = new Set<string>();
    const selectableResidents = this.data.allResidents.filter((residentVM) => this.canSelectResident(residentVM));
    if (this.data.previousResidents) {
      this.data.previousResidents.forEach((residentVM) => {
        if (this.canSelectResident(residentVM)) {
          selected.add(residentVM.resident.id);
        }
      });
    } else {
      if (this.data.residentType) {
        selectableResidents.forEach((res) => {
          if (res.type === this.data.residentType) {
            selected.add(res.resident.id);
          }
        });
      } else {
        selectableResidents.forEach((res) => selected.add(res.resident.id));
      }
    }
    this.defaultSelected = selected;
  }

  public submit(): void {
    const { selectedResidentIds, visibleUnavailableResidentIds, filters } = this.contentComponent.onSubmit();

    //* Check if all landlords / tenants / residents are selected
    let residentType: ResidentType | null = null;
    const landlordIds = new Set<string>();
    const tenantIds = new Set<string>();
    const residentIds = new Set<string>();

    this.data.allResidents.forEach((res) => {
      if (this.canSelectResident(res)) {
        if (res.type === ResidentType.LANDLORD) {
          landlordIds.add(res.resident.id);
        }
        if (res.type === ResidentType.TENANT) {
          tenantIds.add(res.resident.id);
        }
        residentIds.add(res.resident.id);
      }
    });

    if (selectedResidentIds.size > 0) {
      if (SetHlp.equals(selectedResidentIds, landlordIds)) {
        residentType = ResidentType.LANDLORD;
      }
      if (SetHlp.equals(selectedResidentIds, tenantIds)) {
        residentType = ResidentType.TENANT;
      }
    }

    const allSelectableResidentsSelected = residentIds.size > 0 && selectedResidentIds.size === residentIds.size;
    const selectedResidentTypeHasUnavailableResidents =
      residentType !== null &&
      this.data.allResidents.some((residentVM) => residentVM.type === residentType && !this.canSelectResident(residentVM));
    if (selectedResidentTypeHasUnavailableResidents) {
      residentType = null;
    }
    const allResidentsSelectable = this.data.allResidents.every((residentVM) => this.canSelectResident(residentVM));
    const useAllResidentsShortcut = allSelectableResidentsSelected && allResidentsSelectable;

    this.dialogRef.close({
      selectedResidentIds: useAllResidentsShortcut || residentType ? null : Array.from(selectedResidentIds),
      visibleUnavailableResidentIds: Array.from(visibleUnavailableResidentIds),
      selectedFilters: filters,
      residentType
    });
  }

  /** @param residentVM */
  private canSelectResident(residentVM: ReceiverSelectDialogData['allResidents'][number]): boolean {
    return (this.data.phoneStatuses.get(residentVM.resident.id) ?? 'allowed') === 'allowed';
  }
}
